import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'src/components/Button/Button'
import { Headline } from 'src/components/Headline/Headline'
import { IconLoading } from 'src/components/Icons/IconLoading'
import { Rating } from 'src/components/Rating/Rating'
import { fetchProductReviews } from 'src/data/providers/fetchProductReviews'

import { ProductReview } from './ProductReview'
import styles from './ProductReviews.module.scss'

import type { Product } from 'src/data/graphql-generated'
import type { PaginatedProductReviews, ProductRating, ProductReview as ProductReviewType } from 'src/types/Product'

interface ProductReviewsProps {
  product: Product
  rating: ProductRating
  initialReviews: ProductReviewType[]
  initialReviewsMeta: PaginatedProductReviews['meta']
}

export const ProductReviews = ({ product, rating, initialReviews, initialReviewsMeta }: ProductReviewsProps) => {
  const { t } = useTranslation()
  const [reviews, setReviews] = useState<ProductReviewType[]>(initialReviews)
  const [reviewsMeta, setReviewsMeta] = useState<PaginatedProductReviews['meta']>(initialReviewsMeta)
  const [isReviewsLoading, setIsReviewsLoading] = useState(false)

  const {
    variants: { nodes: productVariants },
  } = product

  const fetchMoreReviews = async () => {
    if (reviewsMeta.nextPage) {
      setIsReviewsLoading(true)

      const { data, meta } = await fetchProductReviews({
        skus: productVariants.map((variant) => variant.sku) as string[],
        page: reviewsMeta.nextPage,
      })

      setReviews([reviews, data].flat())
      setReviewsMeta(meta)
      setIsReviewsLoading(false)
    }
  }

  return (
    <div>
      <Headline variant="h3" className={styles.headline}>
        <Rating rating={rating.average} size="lg" />
        {t('pdp.ratingCustomerReviews', { count: reviewsMeta.total })}
      </Headline>

      {reviews.map((review) => (
        <ProductReview review={review} key={review.id} />
      ))}

      {reviewsMeta.hasNextPage && (
        <Button
          variant="secondary"
          disabled={isReviewsLoading}
          startIcon={isReviewsLoading ? <IconLoading stroke={1.6} /> : undefined}
          onClick={() => fetchMoreReviews()}
        >
          {t('common.showMore')}
        </Button>
      )}
    </div>
  )
}
